import { useMemo, useState } from 'react'
import {
    Box,
    Typography,
    Card,
    Divider,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton
} from '@mui/material'
import {
    CalendarToday as CalendarIcon,
    LocationOn as LocationIcon,
    AccessTime as TimeIcon,
    CheckCircle as AcceptIcon,
    Cancel as DeclineIcon,
    PhotoLibrary as PhotoLibraryIcon,
    Home as HomeIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Close as CloseIcon
} from '@mui/icons-material'
import { PrimaryButton, SecondaryButton } from '../index'
import { formatPhoneNumber } from '../../utils/formatPhoneNumber'

// Tour request data types
export interface TourRequest {
    id: string
    status: 'pending' | 'accepted' | 'declined' | 'expired'
    listing: {
        title: string
        neighborhood: string
        address: string
        rent: number
        bedrooms: number
        bathrooms: number
        feature_photo_url?: string | null
        photos: string[]
    }
    landlord: {
        name: string
        avatar?: string | null
    }
    requested_date: string
    requested_time: string
    message?: string
    match_score: number
    rejection_reason?: string
    listing_url: string
    listing_contact_email: string
    listing_contact_number?: string
}

// Match Score Banner Component
const MatchScoreBanner = ({ score }: { score: number }) => {
    return (
        <Chip
            label={`${score}% Match`}
            size="small"
            sx={{
                bgcolor: '#228B22', // Forest green
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '24px',
                '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                }
            }}
        />
    )
}

// Expired Banner Component
const ExpiredBanner = () => {
    return (
        <Chip
            label="Expired"
            size="small"
            sx={{
                bgcolor: 'error.main',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '24px',
                '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                }
            }}
        />
    )
}

// Tour Request Card Component
interface TourRequestCardProps {
    tour: TourRequest
    isExpired?: boolean
    processing?: string | null
    variant: 'pending' | 'scheduled' | 'past'
    onAcceptTour?: (tourId: string) => void
    onDeclineTour?: (tourId: string) => void
    onCancelTour?: (tourId: string) => void
}

export default function TourRequestCard({
    tour,
    isExpired = false,
    processing,
    variant,
    onAcceptTour,
    onDeclineTour,
    onCancelTour
}: TourRequestCardProps) {
    const [photosOpen, setPhotosOpen] = useState(false)
    const photos = tour.listing.photos || []
    const heroPhoto = tour.listing.feature_photo_url || photos[0]
    const parsedRequestedDate = useMemo(() => {
        const [year, month, day] = tour.requested_date.split('-').map(Number)
        return new Date(year, month - 1, day)
    }, [tour.requested_date])
    const formattedRequestedDate = useMemo(
        () =>
            parsedRequestedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
        [parsedRequestedDate]
    )

    return (
        <Card key={tour.id} sx={{ p: 0, overflow: 'hidden', borderRadius: '16px' }}>
            {/* Property Image */}
            {heroPhoto && (
                <Box sx={{ width: '100%', height: 200 }}>
                    <img
                        src={heroPhoto}
                        alt={tour.listing.title}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                </Box>
            )}

            <Box sx={{ p: 3 }}>
                {/* Listing Info */}
                <Box sx={{ mb: 2 }}>
                    {/* Line 1: Neighborhood (left) ←→ Rent (right) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 16, color: 'grey.800' }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'grey.700' }}>
                                {tour.listing.neighborhood}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'grey.800' }}>
                                <span style={{ fontWeight: 800, fontFamily: 'Rye'}}>$</span>{tour.listing.rent.toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Line 2: Bed & Bath (left) ←→ Match Score/Expired (right) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HomeIcon sx={{ fontSize: 16, color: 'grey.800' }} />
                            <Typography variant="body1" color="grey.700" fontWeight={600}>
                                {tour.listing.bedrooms} bed • {tour.listing.bathrooms} bath
                            </Typography>
                        </Box>
                        {isExpired ? (
                            <ExpiredBanner />
                        ) : (
                            <MatchScoreBanner score={tour.match_score} />
                        )}
                    </Box>
                </Box>

                {/* Photos: hero uses feature-first order from the server */}
                {photos.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <SecondaryButton
                            startIcon={<PhotoLibraryIcon />}
                            onClick={() => setPhotosOpen(true)}
                            fullWidth
                            sx={{ py: 1, fontSize: '16px' }}
                        >
                            {photos.length === 1 ? 'View photo' : 'View photos'}
                        </SecondaryButton>
                    </Box>
                )}

                <Dialog
                    open={photosOpen}
                    onClose={() => setPhotosOpen(false)}
                    fullScreen
                >
                    <DialogTitle
                        sx={{
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1.5
                        }}
                    >
                        Photos
                        <IconButton aria-label="Close photos" onClick={() => setPhotosOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {photos.map((url, i) => (
                                <Box
                                    key={`${tour.id}-photo-${i}`}
                                    component="img"
                                    src={url}
                                    alt={tour.listing.title}
                                    sx={{
                                        width: '100%',
                                        maxHeight: 'calc(100vh - 160px)',
                                        objectFit: 'contain',
                                        borderRadius: 1,
                                        bgcolor: 'grey.100'
                                    }}
                                />
                            ))}
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Tour Details */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {variant === 'pending' ? 'Requested Tour Time' : variant === 'scheduled' ? 'Scheduled Tour Time' : 'Tour Date'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'grey.600' }} />
                        <Typography variant="body2">
                            {tour.listing.address}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'grey.600' }} />
                        <Typography variant="body2">
                            {formattedRequestedDate}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <TimeIcon sx={{ fontSize: 16, color: 'grey.600' }} />
                        <Typography variant="body2">
                            {tour.requested_time}
                        </Typography>
                    </Box>
                </Box>

                {/* Tour Giver Contact */}
                {(tour.listing_contact_email || tour.listing_contact_number || (!isExpired && tour.landlord.name)) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                            Tour Giver Contact
                        </Typography>
                        {tour.landlord.name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" color="grey.600">
                                    {tour.landlord.name}
                                </Typography>
                            </Box>
                        )}
                        {tour.listing_contact_email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 16, color: 'grey.600' }} />
                                <Typography variant="body2">
                                    {tour.listing_contact_email}
                                </Typography>
                            </Box>
                        )}
                        {tour.listing_contact_number && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 16, color: 'grey.600' }} />
                                <Typography variant="body2">
                                    {formatPhoneNumber(tour.listing_contact_number)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Message/Notes */}
                {tour.message && (
                    <>
                        {variant === 'pending' && !isExpired && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {tour.message}
                                </Typography>
                            </>
                        )}
                        {(variant === 'scheduled' || variant === 'past') && (
                            <Box sx={{
                                p: 2,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                                mb: 2
                            }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    <span style={{ fontWeight: 600 }}>Tour Note:</span> {tour.message}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                {/* Action Buttons */}
                {!isExpired && variant === 'pending' && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <SecondaryButton
                            startIcon={processing === tour.id ? <CircularProgress size={16} /> : <DeclineIcon />}
                            onClick={() => onDeclineTour?.(tour.id)}
                            disabled={processing === tour.id}
                            sx={{
                                flex: 1,
                                minWidth: '120px',
                                color: 'error.main'
                            }}
                        >
                            Decline
                        </SecondaryButton>
                        <PrimaryButton
                            startIcon={processing === tour.id ? <CircularProgress size={16} /> : <AcceptIcon />}
                            onClick={() => onAcceptTour?.(tour.id)}
                            disabled={processing === tour.id}
                            sx={{
                                flex: 1,
                                minWidth: '120px',
                                bgcolor: 'black',
                                '&:hover': { bgcolor: 'grey.800' }
                            }}
                        >
                            Accept
                        </PrimaryButton>
                    </Box>
                )}
                {variant === 'scheduled' && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <SecondaryButton
                            startIcon={processing === tour.id ? <CircularProgress size={16} /> : <DeclineIcon />}
                            onClick={() => onCancelTour?.(tour.id)}
                            disabled={processing === tour.id}
                            sx={{
                                flex: 1,
                                minWidth: '120px',
                                color: 'error.main'
                            }}
                        >
                            Cancel Tour
                        </SecondaryButton>
                    </Box>
                )}
            </Box>
        </Card>
    )
}

